import type { Partner } from "@expert/shared-types";

import type { SpellCheckState, SpellCheckStatus } from "@/sdk/agent/chat/types";

import type { SpellCheckConfig } from "./config";

export const getSpellCheckMutationKey = (chatId: string) => ["spellCheck", chatId] as const;

export type SpellCheckMetadataStatus =
    | "disabled"
    | "success"
    | "error"
    | "suggestions_accepted"
    | "suggestions_cancelled"
    | "skipped";

export interface SpellCheckMetadataRef {
    spellcheckEnabled: boolean;
    spellcheckStatus?: SpellCheckMetadataStatus;
    spellcheckIssuesFixed?: number;
    originalMessageLength?: number;
}

export interface UseSpellCheckOptions {
    chatId: string;
    sessionId: string;
    partner: Partner;
    /** Whether spellcheck feature is enabled */
    enabled: boolean;
    /** Config object, falls back to SPELLCHECK_CONFIG at the call site */
    config?: SpellCheckConfig;
    /** Current input value */
    localInput: string;
    /** Function to send a message */
    sendMessage: (text: string) => void;
    /** Ref to store metadata for analytics */
    spellCheckMetadataRef: { current: SpellCheckMetadataRef };
    /** Called when user cancels suggestions */
    onCancelWithText: (text: string) => void;
}

export interface UseSpellCheckResult {
    /** Trigger a debounced background spellcheck */
    triggerSpellCheck: (content: string) => void;
    /** Current spellcheck state from store */
    spellCheckState: SpellCheckState | undefined;
    /** Clear the spellcheck state */
    clearSpellCheck: () => void;
    /** Whether a spellcheck is currently in progress */
    isChecking: boolean;
    /** Display status for UI — returns 'idle' when status should be hidden */
    displayStatus: SpellCheckStatus;
    /** Submit message with spellcheck flow */
    handleSubmitMessage: () => Promise<void>;
    /** Send with the corrected text */
    handleSendWithChanges: () => void;
    /** Send with the original text (skip corrections) */
    handleSendOriginal: () => void;
    /** Cancel suggestions and put corrected text in input for editing */
    handleCancelSpellCheck: () => void;
    /** Dismiss the status UI */
    handleDismissStatus: () => void;
}
